import React, { useState, useEffect, useRef } from 'react';
import { 
  Send, 
  Bot, 
  User, 
  BarChart2, 
  TrendingUp, 
  DollarSign, 
  Zap, 
  PauseCircle,
  PlayCircle,
  Settings,
  Sparkles
} from 'lucide-react';

export default function GlowyAIChat() {
  const [messages, setMessages] = useState([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const chatContainerRef = useRef(null);

  // Sample conversation between user and AI
  const sampleConversation = [
    {
      sender: 'user',
      content: 'Hey GlowyAI, how are my marketing campaigns performing this week?',
      timestamp: new Date(Date.now() - 1000 * 60 * 60).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      sender: 'ai',
      content: "Hi there! I've analyzed your campaigns and here's what I found: Your Facebook campaign \"Summer Sale\" is performing exceptionally well with a 24% increase in ROAS compared to last week. However, your Google Ads campaign \"Product Launch\" has seen a 12% decrease in click-through rate.",
      timestamp: new Date(Date.now() - 1000 * 60 * 59).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      sender: 'user',
      content: "That's concerning. Can you analyze what's causing the drop in CTR for the Product Launch campaign?",
      timestamp: new Date(Date.now() - 1000 * 60 * 58).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      sender: 'ai',
      content: "I've analyzed the data and found several potential issues: 1) Your ad creative hasn't been updated in 3 weeks, which may be causing ad fatigue. 2) Your competitors have increased their bids for the same keywords. 3) Your landing page load time has increased by 2.3 seconds, which could be affecting quality score.",
      timestamp: new Date(Date.now() - 1000 * 60 * 57).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      sender: 'user',
      content: 'Can you suggest some optimizations for the Product Launch campaign?',
      timestamp: new Date(Date.now() - 1000 * 60 * 56).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      sender: 'ai',
      content: "Based on my analysis, here are some recommended optimizations:\n\n1. Refresh your ad creative with 3 new variations I've generated based on your best-performing ads\n2. Increase your bid by 15% for your top-converting keywords\n3. Optimize your landing page images to improve load time\n4. Adjust your audience targeting to focus more on the segments that have shown higher engagement\n\nWould you like me to implement any of these changes automatically?",
      timestamp: new Date(Date.now() - 1000 * 60 * 55).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      sender: 'user',
      content: 'Yes, please implement the new ad creatives and increase the bids for top keywords. Also, can you increase the daily budget by 20%?',
      timestamp: new Date(Date.now() - 1000 * 60 * 54).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      sender: 'ai',
      content: "I've implemented the changes:\n\n✅ Uploaded 3 new ad creatives\n✅ Increased bids by 15% for your top 10 converting keywords\n✅ Increased daily budget by 20% (from $500 to $600)\n\nThese changes are now live. I'll monitor the performance closely and provide you with an update in 24 hours. Is there anything else you'd like me to help with?",
      timestamp: new Date(Date.now() - 1000 * 60 * 53).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      sender: 'user',
      content: "That sounds great. Can you also analyze our competitor's ads and see what strategies they're using?",
      timestamp: new Date(Date.now() - 1000 * 60 * 52).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      sender: 'ai',
      content: "I've analyzed your top 5 competitors' ads across Google, Facebook, and Instagram. Here are the key insights:\n\n1. Competitor A is heavily focusing on limited-time offers with countdown timers\n2. Competitor B is using customer testimonials prominently in their ads\n3. Competitor C has increased their video ad spend by 45% in the last month\n4. Competitors are bidding 20% higher on mobile placements compared to desktop\n5. The average competitor landing page has 40% less text than yours\n\nWould you like me to create a detailed competitive analysis report?",
      timestamp: new Date(Date.now() - 1000 * 60 * 51).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      sender: 'user',
      content: 'Yes, please create that report. Also, can you pause our underperforming ad sets that have a CPA higher than $50?',
      timestamp: new Date(Date.now() - 1000 * 60 * 50).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      sender: 'ai',
      content: "I've identified 3 ad sets with CPA higher than $50 and paused them:\n\n1. \"New Customers - Broad\" (CPA: $67.42)\n2. \"Retargeting - 30 days\" (CPA: $58.19)\n3. \"Lookalike - 3%\" (CPA: $72.05)\n\nThis will save approximately $250/day in ad spend that can be reallocated to better-performing campaigns.\n\nI've also generated a comprehensive competitive analysis report. You can view and download it from your dashboard. The report includes visual comparisons of ad creatives, messaging analysis, and bid strategy recommendations.",
      timestamp: new Date(Date.now() - 1000 * 60 * 49).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ];

  // Function to simulate typing effect
  const simulateTyping = (message, callback) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      callback();
    }, 1500); // Simulate typing for 1.5 seconds
  };

  // Add next message from the sample conversation
  const addNextMessage = () => {
    if (isPaused) return;
    
    if (currentMessageIndex < sampleConversation.length) {
      const nextMessage = sampleConversation[currentMessageIndex];
      
      if (nextMessage.sender === 'ai') {
        simulateTyping(nextMessage, () => {
          setMessages(prev => [...prev, nextMessage]);
          setCurrentMessageIndex(prev => prev + 1);
        });
      } else {
        setMessages(prev => [...prev, nextMessage]);
        setCurrentMessageIndex(prev => prev + 1);
      }
    } else {
      // Reset to start the conversation over
      setMessages([]);
      setCurrentMessageIndex(0);
    }
  };

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Add messages with a delay to simulate a real conversation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (messages.length === 0 || 
         (messages.length > 0 && !isTyping && currentMessageIndex < sampleConversation.length)) {
        addNextMessage();
      }
    }, messages.length === 0 ? 1000 : 3000); // First message after 1s, then 3s between messages
    
    return () => clearTimeout(timer);
  }, [messages, isTyping, currentMessageIndex, isPaused]);

  // Toggle pause/play of the conversation
  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  // Reset the conversation
  const resetConversation = () => {
    setMessages([]);
    setCurrentMessageIndex(0);
    setIsTyping(false);
  };

  return (
    <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-200 w-full max-w-4xl mx-auto">
      {/* Chat Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 text-white flex justify-between items-center">
        <div className="flex items-center">
          <img 
            src="/14rob.png" 
            alt="GlowyAI" 
            className="w-8 h-8 object-contain mr-3"
          />
          <div>
            <h3 className="font-bold text-lg">GlowyAI Assistant</h3>
            <p className="text-xs text-blue-100">AI-powered marketing management</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={togglePause} 
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label={isPaused ? "Resume conversation" : "Pause conversation"}
          >
            {isPaused ? <PlayCircle className="w-5 h-5" /> : <PauseCircle className="w-5 h-5" />}
          </button>
          <button 
            onClick={resetConversation} 
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Reset conversation"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Chat Messages */}
      <div 
        ref={chatContainerRef}
        className="p-4 h-[500px] overflow-y-auto bg-gray-50 flex flex-col space-y-4"
      >
        {messages.map((message, index) => (
          <div 
            key={index} 
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div 
              className={`max-w-[80%] rounded-2xl p-4 ${
                message.sender === 'user' 
                  ? 'bg-blue-500 text-white rounded-tr-none' 
                  : 'bg-white border border-gray-200 shadow-sm rounded-tl-none'
              }`}
            >
              <div className="flex items-center mb-2">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
                    message.sender === 'user' 
                      ? 'bg-blue-600' 
                      : 'bg-gradient-to-r from-blue-500 to-purple-500'
                  }`}
                >
                  {message.sender === 'user' 
                    ? <User className="w-4 h-4 text-white" /> 
                    : <Bot className="w-4 h-4 text-white" />
                  }
                </div>
                <div className="flex-1">
                  <p className={`text-xs ${message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                    {message.sender === 'user' ? 'You' : 'GlowyAI'}
                  </p>
                </div>
                <p className={`text-xs ${message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                  {message.timestamp}
                </p>
              </div>
              <div className="whitespace-pre-line">
                {message.content}
              </div>
              
              {/* Action buttons for AI messages with suggestions */}
              {message.sender === 'ai' && message.content.includes('Would you like') && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1.5 rounded-full text-sm transition-colors">
                    Yes, implement all
                  </button>
                  <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full text-sm transition-colors">
                    Show me details first
                  </button>
                </div>
              )}
              
              {/* Quick action buttons for specific AI messages */}
              {message.sender === 'ai' && message.content.includes('analyzed your campaigns') && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button className="flex items-center bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1.5 rounded-full text-sm transition-colors">
                    <BarChart2 className="w-4 h-4 mr-1" /> View full report
                  </button>
                  <button className="flex items-center bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1.5 rounded-full text-sm transition-colors">
                    <TrendingUp className="w-4 h-4 mr-1" /> Optimize campaigns
                  </button>
                </div>
              )}
              
              {/* Budget adjustment buttons */}
              {message.sender === 'ai' && message.content.includes('Increased daily budget') && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button className="flex items-center bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-1.5 rounded-full text-sm transition-colors">
                    <DollarSign className="w-4 h-4 mr-1" /> Adjust budget
                  </button>
                  <button className="flex items-center bg-amber-100 hover:bg-amber-200 text-amber-700 px-3 py-1.5 rounded-full text-sm transition-colors">
                    <Zap className="w-4 h-4 mr-1" /> Auto-optimize
                  </button>
                </div>
              )}
              
              {/* Pause campaign buttons */}
              {message.sender === 'ai' && message.content.includes('paused them') && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button className="flex items-center bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1.5 rounded-full text-sm transition-colors">
                    <PauseCircle className="w-4 h-4 mr-1" /> Pause all campaigns
                  </button>
                  <button className="flex items-center bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1.5 rounded-full text-sm transition-colors">
                    <PlayCircle className="w-4 h-4 mr-1" /> Resume paused ads
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {/* Typing indicator */}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-none p-4 max-w-[80%] shadow-sm">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center mr-2">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chat Input */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex items-center">
          <textarea
            placeholder="Ask GlowyAI about your marketing campaigns..."
            className="flex-1 border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[48px] max-h-[200px] transition-all duration-200 leading-6"
            disabled
            rows={1}
          />
          <button className="ml-2 bg-blue-500 text-white rounded-full p-2 hover:bg-blue-600 transition-colors">
            <Send className="w-5 h-5" />
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full text-xs transition-colors">
            Analyze campaign performance
          </button>
          <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full text-xs transition-colors">
            Optimize ad spend
          </button>
          <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full text-xs transition-colors">
            Create new ad creative
          </button>
        </div>
      </div>
    </div>
  );
} 